<template>
    <div class="pad">
        <slot name="before"></slot>

        <div v-if="!hideSection" :class="bare ? 'overview-bare' : 'box'">
            <div v-if="twoColumn" class="columns is-desktop">
                <!-- Left column -->
                <div class="column is-two-thirds-widescreen">
                    <div class="is-clearfix">
                        <template v-if="editable && !editing && !bare">
                            <a @click="handleEdit" :title="$t('Edit')" class="is-pulled-right">
                                <b-tooltip :label="$t('Edit')" :delay="500" append-to-body>
                                    <b-icon icon="pencil" size="is-small"></b-icon>
                                </b-tooltip>
                            </a>
                        </template>
                        <template v-if="editing && !hideEditButtons">
                            <div class="is-pulled-right buttons are-small m-0">
                                <b-button type="is-primary" :loading="saving" @click="$emit('save')">
                                    {{ $t('Save') }}
                                </b-button>
                                <b-button @click="cancelEdit">
                                    {{ $t('Cancel') }}
                                </b-button>
                            </div>
                        </template>
                        <!-- A page with its own first heading puts it here, so the
                             edit control sits on that line instead of floating
                             above it. -->
                        <slot name="section-title">
                            <p v-if="sectionTitle" class="title is-6 is-uppercase is-pulled-left">
                                {{ $t(sectionTitle) }}
                            </p>
                        </slot>
                    </div>
                    <hr v-if="sectionTitle" class="my-3" />

                    <slot name="fields" :data="data" :editing="editing"></slot>
                </div>

                <!-- Right column -->
                <div class="column is-one-third-widescreen">
                    <slot name="sidebar" :data="data">
                        <div
                            v-if="normalizedTabs.length > 0"
                            class="tabs is-toggle is-fullwidth"
                            style="background: inherit"
                        >
                            <ul>
                                <li
                                    v-for="tab in normalizedTabs"
                                    :key="tab.key"
                                    :class="{ 'is-active': activeTab === tab.key }"
                                >
                                    <a @click="activeTab = tab.key">
                                        <b-icon :icon="tab.icon" size="is-small"></b-icon>
                                        <span>{{ $t(tab.label) }}</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <app-activities
                                v-if="hasTab('activities')"
                                v-show="activeTab === 'activities'"
                                :id="data.id"
                                :type="entityType"
                                :data="data"
                                compact
                                :limit="compactLimit"
                                :more-route="getMoreRoute('activities')"
                            ></app-activities>
                            <app-timesheets
                                v-if="hasTab('timesheets')"
                                v-show="activeTab === 'timesheets'"
                                :id="data.id"
                                :type="entityType"
                                compact
                                :limit="compactLimit"
                                :more-route="getMoreRoute('timesheets')"
                            ></app-timesheets>
                            <app-comments
                                v-if="hasTab('comments')"
                                v-show="activeTab === 'comments'"
                                :id="data.id"
                                :type="entityType"
                                compact
                                :limit="compactLimit"
                                :more-route="getMoreRoute('comments')"
                            ></app-comments>

                            <div></div>
                            <!-- fix .box:not(:last-child) !-->
                        </div>
                    </slot>
                </div>
            </div>

            <!-- Single column layout -->
            <template v-else>
                <div class="is-clearfix">
                    <template v-if="editable && !editing && !bare">
                        <a @click="handleEdit" :title="$t('Edit')" class="is-pulled-right">
                            <b-tooltip :label="$t('Edit')" :delay="500" append-to-body>
                                <b-icon icon="pencil" size="is-small"></b-icon>
                            </b-tooltip>
                        </a>
                    </template>
                    <template v-if="editing && !hideEditButtons">
                        <div class="is-pulled-right buttons are-small m-0">
                            <b-button type="is-primary" :loading="saving" @click="$emit('save')">
                                {{ $t('Save') }}
                            </b-button>
                            <b-button @click="cancelEdit">
                                {{ $t('Cancel') }}
                            </b-button>
                        </div>
                    </template>
                    <slot name="section-title">
                        <p v-if="sectionTitle" class="title is-6 is-uppercase is-pulled-left">
                            {{ $t(sectionTitle) }}
                        </p>
                    </slot>
                </div>
                <hr v-if="sectionTitle" class="my-3" />

                <slot name="fields" :data="data" :editing="editing"></slot>
            </template>
        </div>

        <slot name="after" :data="data" :editing="editing"></slot>
    </div>
</template>

<script setup>
import { ref, computed, inject, useSlots, watch } from 'vue';
import { useRouter } from 'vue-router';
import { wasLastNavigationPop, shouldResumeEditing, markEditing, forgetEditing } from '@/composables/useModuleEntry';
import { useRecordOpenMode } from '@/composables/useRecordOpenMode';

const props = defineProps({
    data: {
        type: Object,
        required: true
    },
    id: {
        type: [String, Number],
        required: true
    },
    // Caption above the fields, with a rule under it. An empty string drops
    // both — for a record whose header already says what this section is, where
    // the caption only repeats the page and its rule doubles the header's.
    sectionTitle: {
        type: String,
        default: 'Information'
    },
    editable: {
        type: Boolean,
        default: true
    },
    // Collapse to a single full-width column. A `#sidebar` slot counts as a
    // sidebar even on a record where it renders nothing, which otherwise leaves
    // a third of the box blank — this lets a page say when that is the case.
    hideSidebar: {
        type: Boolean,
        default: false
    },
    editRoute: {
        type: Object,
        default: null
    },
    inlineEdit: {
        type: Boolean,
        default: false
    },
    saving: {
        type: Boolean,
        default: false
    },
    hideEditButtons: {
        type: Boolean,
        default: false
    },
    hideSection: {
        type: Boolean,
        default: false
    },
    // Drop the card and the pencil, and draw the fields on the page itself.
    // For a fiche whose own header carries the edit control — the card would be
    // a second frame inside the panel's, and the pencil a second way in.
    bare: {
        type: Boolean,
        default: false
    },

    entityType: {
        type: String,
        default: ''
    },
    // The list this fiche is opened from, as the column manager keys it — the
    // user's "Opening a record" choice is stored per list. Only needed where
    // `entityType` does not name it on its own (one fiche, several lists).
    objectType: {
        type: String,
        default: ''
    },
    routePrefix: {
        type: String,
        default: ''
    },
    sidebarTabs: {
        type: Array,
        default: () => []
    },
    defaultTab: {
        type: String,
        default: ''
    },
    compactLimit: {
        type: Number,
        default: 3
    }
});

const emit = defineEmits(['save', 'cancel-edit']);

const slots = useSlots();
const router = useRouter();
const parentEdit = inject('parentEdit', null);
const { opensReadyToEdit } = useRecordOpenMode(
    () => props.entityType,
    () => props.objectType
);

const editing = ref(false);

// Resume inline edit mode when the user returns to this record via
// history.back/forward (see useModuleEntry); a fresh link entry starts in read
// mode. The pop status is captured at setup, but the decision waits for the
// record id, which is only known once the async data has loaded. Turning
// `editing` on then makes the parent's `editing` watcher fire startEdit exactly
// as a manual edit click would.
// Re-evaluated per record: `editable` is usually a per-record permission check
// (a task the user may not write), and the component is reused across records —
// a snapshot taken at setup would carry the first record's rights to the rest.
const canInlineEdit = computed(() => props.inlineEdit && props.editable);
const arrivedViaPop = wasLastNavigationPop();
let resumeResolved = false;

async function resolveEditResume(id, switchedRecord = false) {
    if (!id) {
        return;
    }
    // The history-back resume is a one-shot decision about how this component was
    // entered; later record switches inside it are not pop navigations.
    if (canInlineEdit.value && !resumeResolved) {
        resumeResolved = true;
        if (arrivedViaPop && shouldResumeEditing(props.entityType, id)) {
            editing.value = true;
            return;
        }
        // A fresh/forward view of a record abandons any prior edit flow.
        forgetEditing();
    }
    // Preference: open the record ready to edit rather than read-only — this
    // user's answer for the list it came from, the organization's otherwise.
    // Re-applied on every record switch, not just the first — switching opens a
    // fiche, and it opens the way fiches open, whichever mode the previous record
    // was left in (see utils/openInEditMode.js).
    if (await opensReadyToEdit(canInlineEdit)) {
        editing.value = true;
    } else if (switchedRecord) {
        editing.value = false;
        forgetEditing();
    }
}

function handleEdit() {
    if (props.inlineEdit) {
        editing.value = true;
    } else if (props.editRoute) {
        router.push(props.editRoute);
    } else if (parentEdit) {
        parentEdit(props.data);
    }
}

function cancelEdit() {
    editing.value = false;
    emit('cancel-edit');
}

function setEditing(value) {
    editing.value = value;
}

// Track which record is being edited so history.back can resume it. The id
// watcher resolves the pending resume once data loads, and re-keys when
// switching records while editing (the component is reused, so `id` changes
// without `editing` toggling).
if (props.inlineEdit) {
    watch(editing, value => markEditing(props.entityType, props.id, value));
    watch(
        () => props.id,
        async (newId, oldId) => {
            const switchedRecord = oldId !== undefined && String(newId) !== String(oldId);
            const wasEditing = editing.value;

            // Awaited: the mode may depend on this user's per-list choice, which
            // is fetched once per session. Everything below reads the mode the
            // resume settled on, so it has to wait for it.
            await resolveEditResume(newId, switchedRecord);

            if (editing.value) {
                markEditing(props.entityType, newId, true);
            } else if (wasEditing) {
                // Dropping out of edit mode by switching record is a cancel: the
                // parent's edit buffer still holds the record we just left.
                emit('cancel-edit');
            }
        },
        { immediate: true }
    );
}

defineExpose({ editing, setEditing });

const TAB_DEFAULTS = {
    activities: { key: 'activities', label: 'Activities', icon: 'timeline-clock-outline' },
    comments: { key: 'comments', label: 'Comments', icon: 'comment-text-outline' },
    timesheets: { key: 'timesheets', label: 'Timesheets', icon: 'clock-outline' }
};

const normalizedTabs = computed(() => {
    return props.sidebarTabs.map(tab => {
        if (typeof tab === 'string') {
            return TAB_DEFAULTS[tab] || { key: tab, label: tab, icon: 'dots-horizontal' };
        }
        return tab;
    });
});

const twoColumn = computed(() => {
    if (props.hideSidebar) {
        return false;
    }

    return normalizedTabs.value.length > 0 || !!slots.sidebar;
});

const activeTab = ref(props.defaultTab || (normalizedTabs.value.length > 0 ? normalizedTabs.value[0].key : ''));

function hasTab(key) {
    return normalizedTabs.value.some(t => t.key === key);
}

function getMoreRoute(tabKey) {
    if (!props.routePrefix) return null;
    return {
        name: `${props.routePrefix}-${tabKey}`,
        params: { id: props.data.id }
    };
}
</script>
